import { ReactNode } from "react";

// Gradient Border Card with animated shimmer
interface GradientBorderCardProps {
  children: ReactNode;
  className?: string;
  borderWidth?: number;
}

export function GradientBorderCard({ children, className = "", borderWidth = 2 }: GradientBorderCardProps) {
  return (
    <div className={`relative group ${className}`}>
      {/* Animated gradient border */}
      <div 
        className="absolute -inset-px rounded-2xl bg-gradient-to-r from-accent via-primary to-accent bg-[length:200%_auto] animate-gradient-border opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ padding: borderWidth }}
      />
      {/* Inner content */}
      <div className="relative bg-card rounded-2xl h-full">
        {children}
      </div>
    </div>
  );
}

// Magnetic Button Effect - CSS only
interface MagneticButtonProps {
  children: ReactNode;
  className?: string;
  strength?: number;
}

export function MagneticButton({ children, className = "", strength = 0.3 }: MagneticButtonProps) {
  return (
    <div
      className={`inline-block transition-transform duration-200 ease-out hover:scale-105 active:scale-95 ${className}`}
    >
      {children}
    </div>
  );
}

// Glow Card Effect - CSS only
interface GlowCardProps {
  children: ReactNode;
  className?: string;
  glowColor?: string;
}

export function GlowCard({ children, className = "", glowColor = "accent" }: GlowCardProps) {
  return (
    <div className={`relative group ${className}`}>
      <div
        className={`absolute -inset-1 rounded-2xl bg-${glowColor}/20 blur-xl opacity-0 scale-[0.8] group-hover:opacity-100 group-hover:scale-100 transition-all duration-300`}
      />
      <div className="relative">{children}</div>
    </div>
  );
}

// Section Wave Divider
interface WaveDividerProps {
  className?: string;
  flip?: boolean;
  color?: string;
}

export function WaveDivider({ className = "", flip = false, color = "bg-background" }: WaveDividerProps) {
  return (
    <div className={`w-full overflow-hidden leading-none ${flip ? "rotate-180" : ""} ${className}`}>
      <svg 
        viewBox="0 0 1200 120" 
        preserveAspectRatio="none" 
        className={`w-full h-16 md:h-24 ${color}`}
        fill="currentColor"
      >
        <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" />
      </svg>
    </div>
  );
}

// Curved Section Divider
interface CurvedDividerProps {
  className?: string;
  flip?: boolean;
}

export function CurvedDivider({ className = "", flip = false }: CurvedDividerProps) {
  return (
    <div className={`w-full overflow-hidden leading-none ${flip ? "rotate-180" : ""} ${className}`}>
      <svg 
        viewBox="0 0 1200 120" 
        preserveAspectRatio="none" 
        className="w-full h-12 md:h-20 text-background"
        fill="currentColor"
      >
        <path d="M600,112.77C268.63,112.77,0,65.52,0,7.23V120H1200V7.23C1200,65.52,931.37,112.77,600,112.77Z" />
      </svg>
    </div>
  );
}

// Noise Texture Overlay
interface NoiseOverlayProps {
  opacity?: number;
  className?: string;
}

export function NoiseOverlay({ opacity = 0.03, className = "" }: NoiseOverlayProps) {
  return (
    <div 
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        opacity,
      }}
    />
  );
}

// Floating Badge with Pulse - CSS only
interface FloatingBadgeProps {
  children: ReactNode;
  className?: string;
}

export function FloatingBadge({ children, className = "" }: FloatingBadgeProps) {
  return (
    <div className={`inline-flex items-center gap-2 animate-float ${className}`}>
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
      </span>
      {children}
    </div>
  );
}

// Shimmer Text Effect - CSS only
interface ShimmerTextProps {
  children: ReactNode;
  className?: string;
}

export function ShimmerText({ children, className = "" }: ShimmerTextProps) {
  return (
    <span className={`relative inline-block overflow-hidden ${className}`}>
      <span className="relative z-10">{children}</span>
      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
    </span>
  );
}

// Ripple Effect Button - CSS only
interface RippleEffectProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}

export function RippleEffect({ children, className = "", onClick }: RippleEffectProps) {
  return (
    <div
      className={`relative overflow-hidden transition-transform duration-200 active:scale-95 ${className}`}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

// Spotlight Effect for Cards - CSS only
interface SpotlightCardProps {
  children: ReactNode;
  className?: string;
}

export function SpotlightCard({ children, className = "" }: SpotlightCardProps) {
  return (
    <div className={`relative overflow-hidden group ${className}`}>
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{
          background: "radial-gradient(circle at 50% 50%, hsl(var(--accent) / 0.15), transparent 50%)",
        }}
      />
      {children}
    </div>
  );
}

// Animated Underline
interface AnimatedUnderlineProps {
  children: ReactNode;
  className?: string;
}

export function AnimatedUnderline({ children, className = "" }: AnimatedUnderlineProps) {
  return (
    <span className={`relative inline-block group ${className}`}>
      {children}
      <span className="absolute bottom-0 left-0 w-full h-0.5 bg-accent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
    </span>
  );
}
